import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { Plus, X, Save } from "lucide-react";

// Stable client-side keys for editable rows (names are editable, so they can't be keys).
const withKeys = (arr) => (arr || []).map((x) => ({ ...x, _k: x._k || Math.random().toString(36).slice(2, 10) }));
const stripKeys = (arr) => (arr || []).map(({ _k, ...x }) => x);

/** Editable list (breeds or colours) — add / rename / enable-disable / delete. */
function ItemList({ items, onChange, kind, atype }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const name = draft.trim();
    if (!name) return;
    if (items.some((i) => i.name.toLowerCase() === name.toLowerCase())) { toast.error("Already exists"); return; }
    onChange([...items, { name, enabled: true, _k: Math.random().toString(36).slice(2, 10) }]);
    setDraft("");
  };
  return (
    <div className="space-y-2">
      {items.map((it, idx) => (
        <div key={it._k || idx} className="flex items-center gap-2" data-testid={`master-${atype}-${kind}-row-${idx}`}>
          <Input className="am-input h-9 flex-1" value={it.name}
            onChange={(e) => onChange(items.map((x, i) => (i === idx ? { ...x, name: e.target.value } : x)))} />
          <Switch checked={it.enabled}
            onCheckedChange={(v) => onChange(items.map((x, i) => (i === idx ? { ...x, enabled: v } : x)))}
            data-testid={`master-${atype}-${kind}-toggle-${idx}`} />
          <button onClick={() => onChange(items.filter((_, i) => i !== idx))}
            className="w-8 h-8 rounded-full grid place-items-center text-red-700 hover:bg-red-50"
            data-testid={`master-${atype}-${kind}-delete-${idx}`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Input className="am-input h-9 flex-1" placeholder="Add new…" value={draft}
          onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          data-testid={`master-${atype}-${kind}-add-input`} />
        <Button size="sm" variant="outline" className="rounded-full" onClick={add} data-testid={`master-${atype}-${kind}-add-btn`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

export default function AdminMasterData() {
  const { t } = useT();
  const [master, setMaster] = useState([]);
  const [saving, setSaving] = useState(null);

  useEffect(() => {
    api.get("/master/animals").then((r) => {
      const rows = Array.isArray(r.data) ? r.data : [];
      setMaster(rows.map((m) => ({ ...m, breeds: withKeys(m.breeds), colours: withKeys(m.colours) })));
    }).catch(() => {});
  }, []);

  const patchType = (atype, changes) =>
    setMaster((ms) => ms.map((m) => (m.animal_type === atype ? { ...m, ...changes } : m)));

  const save = async (m) => {
    if (m.breeds.some((b) => !b.name.trim()) || m.colours.some((c) => !c.name.trim())) {
      toast.error("Names cannot be empty"); return;
    }
    setSaving(m.animal_type);
    try {
      const r = await api.patch(`/admin/master/animals/${m.animal_type}`, {
        default_gender: m.default_gender,
        breeds: stripKeys(m.breeds),
        colours: stripKeys(m.colours),
      });
      patchType(m.animal_type, { ...r.data, breeds: withKeys(r.data.breeds), colours: withKeys(r.data.colours) });
      toast.success(`${m.animal_type} ${t("master.saved")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("msg.saveFailed"));
    } finally { setSaving(null); }
  };

  return (
    <DashboardShell title={t("master.title")} subtitle={t("master.subtitle")}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {master.map((m) => (
          <div key={m.animal_type} className="am-card p-5" data-testid={`master-card-${m.animal_type}`}>
            <div className="flex items-center gap-3 mb-4">
              <h2 className="font-display text-lg font-bold capitalize flex-1">{m.animal_type}</h2>
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{t("master.defaultGender")}</span>
                <Select value={m.default_gender} onValueChange={(v) => patchType(m.animal_type, { default_gender: v })}>
                  <SelectTrigger className="am-input h-9 w-28" data-testid={`master-${m.animal_type}-gender`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{t("master.breeds")}</div>
                <ItemList items={m.breeds} kind="breed" atype={m.animal_type}
                  onChange={(items) => patchType(m.animal_type, { breeds: items })} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{t("master.colours")}</div>
                <ItemList items={m.colours} kind="colour" atype={m.animal_type}
                  onChange={(items) => patchType(m.animal_type, { colours: items })} />
              </div>
            </div>
            <Button className="am-btn-primary w-full mt-4" onClick={() => save(m)} disabled={saving === m.animal_type}
              data-testid={`master-${m.animal_type}-save`}>
              <Save className="w-4 h-4 mr-1.5" /> {saving === m.animal_type ? "…" : t("master.save")}
            </Button>
          </div>
        ))}
      </div>
    </DashboardShell>
  );
}
